import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'how-to-request-your-police-records';

export const RequestPoliceRecords = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        Police involvement in a care leaver&apos;s childhood is more common than people expect —
        being reported missing from a placement, a welfare check, an incident at school, or
        being a witness or victim in something investigated at the time. All of that can leave a
        record, and you have a right to see what the police hold about you.
      </p>
      <p>
        Police records work slightly differently from most other SARs, because some of what the
        police hold is processed for a specific <strong className="text-ink">law enforcement</strong>{' '}
        purpose, covered by Part 3 of the{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2018/12/contents">
          Data Protection Act 2018
        </SourceLink>{' '}
        rather than the UK GDPR. Your right of access still applies either way — it is set out
        directly for law enforcement processing in{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2018/12/section/45">
          section 45 of the Act
        </SourceLink>{' '}
        — but it is worth knowing the distinction, since it affects which exemptions a force can
        rely on.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Who to ask" title="ACRO, or the force directly">
      <GuideChecklist
        items={[
          'For most personal police records — including anything on the Police National Computer — you can make a subject access request to ACRO Criminal Records Office, which handles this centrally on behalf of forces in England and Wales.',
          "For records specific to a particular force's own local systems — an incident you remember happening in a specific area, for instance — it can be worth writing to that force's own data protection team directly, alongside or instead of ACRO.",
          'Scotland and Northern Ireland have their own separate routes; Police Scotland and the Police Service of Northern Ireland each handle their own subject access requests.',
        ]}
      />
      <p>
        The national starting point is{' '}
        <SourceLink href="https://www.gov.uk/copy-of-police-records">
          gov.uk&apos;s guide to getting a copy of your police records
        </SourceLink>
        , which explains the current process and where to send a request.
      </p>
    </GuideSection>

    <GuideSection eyebrow="What to ask for" title="What might exist, from a care background">
      <GuideChecklist
        items={[
          'Missing person reports — if you were ever reported missing from a foster placement or children\'s home, there is very often a police record of it, sometimes with more detail than the placement\'s own records.',
          'Any safeguarding referrals or intelligence reports naming you.',
          'Witness or victim statements, if you were involved in an investigation in either capacity.',
          'Records of any welfare or wellbeing checks carried out on you.',
          'Records of any caution, reprimand or conviction, if applicable.',
        ]}
      />
    </GuideSection>

    <GuideNote>
      <p>
        <strong>If you need a police certificate for something specific</strong> — a visa
        application, working abroad, some professional checks — that is usually a different,
        paid service (an ACPO/ACRO police certificate), not a SAR. A SAR is the free route to
        see your own personal data; a certificate is a formal document confirming certain
        conviction information for an official purpose. Don&apos;t pay for one if what you actually
        want is just to see your own record.
      </p>
    </GuideNote>

    <GuideCTA />

    <GuideSection eyebrow="What happens next" title="Deadlines and redactions">
      <p>
        The same <strong className="text-ink">one-month</strong> deadline applies, extendable for
        complex requests. Redactions are more common in police records than most other kinds,
        because they can legitimately protect ongoing investigations, other individuals named in
        the same record, or intelligence sources — but a redaction still has to be justified
        against a specific, identifiable reason, not applied wholesale because the record is
        police-held.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="police-records-faq">
        <GuideFaqItem
          q="I was reported missing several times as a teenager. Will there be a record for each time?"
          a="Usually, yes — each missing episode is typically logged separately, sometimes with a return interview record attached. It's worth asking specifically for missing-from-care records if you remember this happening, since it can otherwise be easy to miss among general police records."
        />
        <GuideFaqItem
          q="Can I see records about an investigation into someone who harmed me?"
          a="You can request records that are your own personal data — including your own witness or victim statement, and how you were involved. Information solely about the investigation into someone else, or their own personal data, is more likely to be withheld, particularly if a case is still live."
        />
        <GuideFaqItem
          q="What if I don't know which force was involved?"
          a="Start with ACRO, which searches centrally, or think about where the incident happened rather than where you live now — a missing-from-care report, for instance, is usually logged by the force covering the placement's area, not necessarily your current or childhood home area."
        />
        <GuideFaqItem
          q="Is there a fee?"
          a="No, a standard subject access request is free, in the same way as any other SAR — this is different from a paid police certificate for visa or employment purposes."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        The ICO has a dedicated page on{' '}
        <SourceLink href="https://ico.org.uk/for-the-public/crime/">
          crime and your data held by the police
        </SourceLink>
        . The legal right of access for law enforcement processing sits in{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2018/12/section/45">
          section 45 of the Data Protection Act 2018
        </SourceLink>
        . For requests to other, non-police organisations — where the ordinary Article 15 UK
        GDPR right applies instead — see our{' '}
        <Link to="/guides/what-is-a-subject-access-request" className="text-brand underline hover:opacity-90">
          general guide to SARs
        </Link>
        .
      </p>
    </GuideSection>

    <GuideCTA
      heading="We can request your police records too"
      body="Add the police force, or let us point you to ACRO, and we'll help draft the request and keep track of the deadline alongside everything else."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default RequestPoliceRecords;
